import { useState, type FormEvent } from "react";
import { APP_ENV, SIGNOUT_REASON, TURNSTILE_SITE_KEY, auth, getSession } from "../lib/api";
import { Turnstile } from "../components/Turnstile";
import { useSession } from "../lib/session";
import { ErrorBox, Field } from "../components/ui";

type Mode = "password" | "link" | "first" | "reset";

export default function Login({ noProfile }: { noProfile?: boolean }) {
  const { refresh } = useSession();
  // ?join=email from the invite email: straight to "Create your account"
  const join = new URLSearchParams(location.search).get("join");
  const [mode, setMode] = useState<Mode>(join ? "first" : "password");
  const [email, setEmail] = useState(join ?? "");
  const [reason] = useState<string | null>(() => { try { const r = localStorage.getItem(SIGNOUT_REASON); localStorage.removeItem(SIGNOUT_REASON); return r; } catch { return null; } });
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [tries, setTries] = useState(0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setSent(null);
    try {
      if (TURNSTILE_SITE_KEY && !captcha) throw new Error("One moment: checking you're not a bot. Try again in a few seconds.");
      const c = captcha ?? undefined;
      if (mode === "password") await auth.signInWithPassword(email, password, c);
      else if (mode === "link") { await auth.sendLink(email, c); setSent("Check your email for a sign-in link. It works once, for one hour."); }
      else if (mode === "reset") { await auth.sendReset(email, c); setSent("Check your email for a link to set a new password."); }
      else {
        if (password.length < 10) throw new Error("Use at least 10 characters for your password.");
        const r = await auth.signUp(email, password, c);
        if (r === "confirm-email") setSent("Check your email and click the link to confirm your account, then sign in.");
      }
    } catch (err) { setError(err as Error); } finally { setBusy(false); setTries((n) => n + 1); }
  }

  return (
    <div className="login">
      <div className="login-art">
        <div className="lb"><img src="/logo.png" alt="" /><span>Royal Compass <em>Advisory</em></span></div>
        <div>
          <h1>Every relationship, <em>clearly set.</em></h1>
          <p>Your team, your leads and your clients in one place. Sign in to see who needs you today.</p>
        </div>
        <span className="small" style={{ color: "#8d90b8", position: "relative", zIndex: 1 }}>{APP_ENV !== "production" ? "Staging copy: test data only" : "Private system. Access by invitation."}</span>
      </div>
      <div className="login-form">
        <form onSubmit={submit}>
          {noProfile ? (
            <>
              <h2>Waiting for access</h2>
              <p className="small">You're signed in as <b style={{ display: "inline" }}>{getSession()?.user.email}</b>, but this email hasn't been given access yet. Ask the owner to invite exactly this email in Settings → Users &amp; permissions.</p>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn primary" onClick={() => refresh()}>Check again</button>
                <button type="button" className="btn" onClick={() => auth.signOut()}>Sign out</button>
              </div>
            </>
          ) : (
            <>
              <h2>{mode === "first" ? "Create your account" : mode === "reset" ? "Reset your password" : "Sign in"}</h2>
              {mode === "first" && <p className="small muted">Use the email you were invited with.</p>}
              {reason && mode === "password" && <p className="small" role="status" style={{ background: "var(--sunk)", padding: "8px 10px", borderRadius: 8 }}>{reason}</p>}
              <ErrorBox error={error} />
              {sent && <div className="pill warm" style={{ whiteSpace: "normal", padding: "8px 12px", borderRadius: 10 }}>{sent}</div>}
              <Field label="Email"><input className="input" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
              {(mode === "password" || mode === "first") && (
                <Field label={mode === "first" ? "Choose a password (10+ characters)" : "Password"}>
                  <input className="input" type="password" autoComplete={mode === "first" ? "new-password" : "current-password"} required value={password} onChange={(e) => setPassword(e.target.value)} />
                </Field>
              )}
              <Turnstile onToken={setCaptcha} resetKey={tries} />
              <button className="btn primary" disabled={busy}>
                {busy ? "One moment…" : mode === "password" ? "Sign in" : mode === "link" ? "Email me a sign-in link" : mode === "reset" ? "Email me a reset link" : "Create account"}
              </button>
              <div style={{ display: "grid", gap: 6, justifyItems: "start" }} className="small">
                {mode !== "password" && <button type="button" className="btn ghost small" onClick={() => setMode("password")}>Sign in with a password</button>}
                {mode !== "link" && <button type="button" className="btn ghost small" onClick={() => setMode("link")}>Email me a sign-in link instead</button>}
                {mode !== "reset" && <button type="button" className="btn ghost small" onClick={() => setMode("reset")}>Forgot your password?</button>}
                {mode !== "first" && <button type="button" className="btn ghost small" onClick={() => setMode("first")}>First time here? Create your account</button>}
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export function SetPassword() {
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  return (
    <div className="login-form" style={{ minHeight: "100vh" }}>
      <form onSubmit={async (e) => { e.preventDefault(); try { if (pw.length < 10) throw new Error("Use at least 10 characters."); await auth.setPassword(pw); setDone(true); } catch (err) { setError(err as Error); } }}>
        <h2>Set a new password</h2>
        <ErrorBox error={error} />
        {done ? <a className="btn primary" href="/">Continue</a> : <>
          <Field label="New password (10+ characters)"><input className="input" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} /></Field>
          <button className="btn primary">Save password</button>
        </>}
      </form>
    </div>
  );
}
